import { FC, useMemo } from 'react';

import { StartIcon, TTranslator } from '@smartshell/ui-kit';

import { TLocalesKeys } from 'localization';
import { IShortcut } from 'schemas';

import s from './ShellShortcutCatalog.module.scss';
import { TShellTopPanelTab } from './ShellTopPanel';

interface IShellShortcutCatalogProps {
  tab: TShellTopPanelTab;
  shortcuts: IShortcut[];
  selectedGroupId: number | null;
  searchValue: string;
  isGuest: boolean;
  t: TTranslator<TLocalesKeys>;
}

const getShortcutType = (tab: TShellTopPanelTab): IShortcut['type'] =>
  tab === 'games' ? 'GAME' : 'APPLICATION';

const normalizeSearchValue = (value: string): string =>
  value.trim().toLocaleLowerCase();

export const ShellShortcutCatalog: FC<IShellShortcutCatalogProps> = ({
  tab,
  shortcuts,
  selectedGroupId,
  searchValue,
  isGuest,
  t,
}) => {
  const visibleShortcuts = useMemo(() => {
    const shortcutType = getShortcutType(tab);
    const normalizedSearchValue = normalizeSearchValue(searchValue);

    return shortcuts
      .filter((shortcut) => shortcut.type === shortcutType)
      .filter(
        (shortcut) =>
          selectedGroupId === null || shortcut.group_id === selectedGroupId
      )
      .filter((shortcut) =>
        normalizeSearchValue(shortcut.title).includes(normalizedSearchValue)
      )
      .sort((left, right) => left.sort - right.sort || left.id - right.id);
  }, [searchValue, selectedGroupId, shortcuts, tab]);

  const isGamesTab = tab === 'games';

  return (
    <section
      className={s.ssShellShortcutCatalog}
      aria-label={t(
        isGamesTab ? 'shell.tabs.games' : 'shell.tabs.applications'
      )}
      data-testid="ss-shell-shortcut-catalog"
      data-shortcut-type={getShortcutType(tab)}
    >
      <div
        className={
          isGamesTab ? s.ssShellShortcutGames : s.ssShellShortcutApplications
        }
        role="list"
      >
        {visibleShortcuts.map((shortcut) =>
          isGamesTab ? (
            <GameShortcutCard
              key={shortcut.id}
              shortcut={shortcut}
              isGuest={isGuest}
              t={t}
            />
          ) : (
            <ApplicationShortcutCard key={shortcut.id} shortcut={shortcut} />
          )
        )}
      </div>
    </section>
  );
};

interface IShortcutCardProps {
  shortcut: IShortcut;
}

interface IGameShortcutCardProps extends IShortcutCardProps {
  isGuest: boolean;
  t: TTranslator<TLocalesKeys>;
}

const ShortcutImage: FC<IShortcutCardProps> = ({ shortcut }) => {
  if (!shortcut.main_picture) {
    return <span className={s.ssShellShortcutImagePlaceholder} aria-hidden />;
  }

  return <img src={shortcut.main_picture} alt={shortcut.title} />;
};

const getGuestRestrictionAge = (ageRating: number | null): 16 | 18 =>
  ageRating === 18 ? 18 : 16;

const GameShortcutCard: FC<IGameShortcutCardProps> = ({
  shortcut,
  isGuest,
  t,
}) => {
  const isRestrictedForGuest = isGuest && shortcut.only_for_registered;
  const guestRestrictionAge = getGuestRestrictionAge(shortcut.age_rating);

  return (
    <article
      className={s.ssShellShortcutGameCard}
      role="listitem"
      data-guest-restricted={String(isRestrictedForGuest)}
    >
      {isRestrictedForGuest ? (
        <div className={s.ssShellShortcutRestrictedCover}>
          <img
            className={s.ssShellShortcutRestrictedBackground}
            src="/shell/game-cover.svg"
            alt=""
          />
          <img
            className={s.ssShellShortcutRestrictedAgeIcon}
            src={`/shell/age-${guestRestrictionAge}.svg`}
            alt=""
          />
          <span className={s.ssShellShortcutRestrictedDescription}>
            {t('shell.shortcut.restricted', guestRestrictionAge)}
          </span>
        </div>
      ) : (
        <div className={s.ssShellShortcutGameCover}>
          <ShortcutImage shortcut={shortcut} />
        </div>
      )}
      <span className={s.ssShellShortcutGameTitle}>{shortcut.title}</span>
      {shortcut.age_rating !== null && (
        <div className={s.ssShellShortcutGameTags}>
          <span className={s.ssShellShortcutAgeRating}>
            {`${
              isRestrictedForGuest ? guestRestrictionAge : shortcut.age_rating
            }+`}
          </span>
        </div>
      )}
      {!isRestrictedForGuest && (
        <span className={s.ssShellShortcutGameAction} aria-hidden>
          <StartIcon color="currentColor" size="l" />
        </span>
      )}
    </article>
  );
};

const ApplicationShortcutCard: FC<IShortcutCardProps> = ({ shortcut }) => (
  <article className={s.ssShellShortcutApplicationCard} role="listitem">
    <div className={s.ssShellShortcutApplicationIcon}>
      <ShortcutImage shortcut={shortcut} />
    </div>
    <span className={s.ssShellShortcutApplicationTitle}>{shortcut.title}</span>
  </article>
);
